// packages/utils/src/score-transform.ts
// FRAME v2 — Piecewise Linear Score Transform
//
// Converts honest-academic raw scores (1.0–10.0) to consumer-friendly
// FRAME scores. Preserves strict monotonic ordering.
// Validated: 75/75 tests, 40 calibration personas, mean 6.98, stdev 1.60
//
// This module is the SINGLE SOURCE OF TRUTH for the transform.
// webScorer.ts, mobile ScoringService, Lambda, and B2B API all import from here.

/** Anchor points: [rawScore, frameScore] */
export const TRANSFORM_ANCHORS: [number, number][] = [
  [1.0, 3.5],   // Hard floor — worst real portrait (slight gradient preserves differentiation)
  [2.0, 4.0],   // Heavily penalized portraits
  [3.0, 4.5],
  [4.0, 5.5],
  [5.0, 6.5],   // True median → above psychological midpoint
  [5.5, 7.0],
  [6.0, 7.5],
  [6.5, 8.0],
  [7.0, 8.5],
  [7.5, 9.0],
  [8.0, 9.3],
  [9.0, 9.7],
  [10.0, 10.0], // Perfect — all dimensions firing
];

/**
 * Transform raw academic score to consumer-friendly FRAME score.
 *
 * Guarantees:
 * - Monotonically non-decreasing
 * - Real portraits (raw >= 1.0) always produce >= 3.5, raw >= 2.0 produces >= 4.0
 * - Only raw 10.0 produces FRAME 10.0
 */
export function transformScore(raw: number): number {
  if (raw <= TRANSFORM_ANCHORS[0][0]) return TRANSFORM_ANCHORS[0][1];
  if (raw >= TRANSFORM_ANCHORS[TRANSFORM_ANCHORS.length - 1][0]) return TRANSFORM_ANCHORS[TRANSFORM_ANCHORS.length - 1][1];

  for (let i = 0; i < TRANSFORM_ANCHORS.length - 1; i++) {
    const [r1, f1] = TRANSFORM_ANCHORS[i];
    const [r2, f2] = TRANSFORM_ANCHORS[i + 1];
    if (raw >= r1 && raw <= r2) {
      const t = r2 !== r1 ? (raw - r1) / (r2 - r1) : 0;
      return Math.round((f1 + t * (f2 - f1)) * 10) / 10;
    }
  }
  return 3.5; // Fallback — should never reach
}

/**
 * Inverse transform: FRAME score → approximate raw score.
 * Used for calibration, threshold mapping, and display logic.
 */
export function inverseTransform(frame: number): number {
  if (frame <= TRANSFORM_ANCHORS[0][1]) return TRANSFORM_ANCHORS[0][0];
  if (frame >= TRANSFORM_ANCHORS[TRANSFORM_ANCHORS.length - 1][1]) return TRANSFORM_ANCHORS[TRANSFORM_ANCHORS.length - 1][0];

  for (let i = 0; i < TRANSFORM_ANCHORS.length - 1; i++) {
    const [r1, f1] = TRANSFORM_ANCHORS[i];
    const [r2, f2] = TRANSFORM_ANCHORS[i + 1];
    if (frame >= f1 && frame <= f2) {
      const t = f2 !== f1 ? (frame - f1) / (f2 - f1) : 0;
      return Math.round((r1 + t * (r2 - r1)) * 100) / 100;
    }
  }
  return 5.0;
}
